import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Activity } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: "/programs", label: "Programs" },
    { section: "manifesto", label: "Manifesto" },
    { section: "science", label: "Science" },
  ];

  const goToSection = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (loc.pathname !== "/") {
      nav("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const linkCls = "text-sm text-soma-muted hover:text-white transition-colors duration-300 relative group";
  const underline = <span className="absolute -bottom-1 left-0 w-0 h-px bg-soma-primary transition-all duration-300 group-hover:w-full" />;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "glass py-3 border-b border-white/10" : "py-6 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <Activity className="w-5 h-5 text-soma-primary transition-transform duration-500 group-hover:rotate-12" strokeWidth={2.5} />
          <span className="font-display font-bold text-xl tracking-tight text-white">neurofdback.ca</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => l.to ? (
            <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className={linkCls}>
              {l.label}{underline}
            </Link>
          ) : (
            <a key={l.label} href={`/#${l.section}`} onClick={goToSection(l.section)} data-testid={`nav-${l.label.toLowerCase()}`} className={linkCls}>
              {l.label}{underline}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm text-soma-accent hover:text-white transition-colors">Admin</Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm text-soma-muted hover:text-white transition-colors">Dashboard</Link>
              <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }}
                className="text-sm px-5 py-2 rounded-full border border-white/15 hover:border-soma-primary hover:text-soma-primary transition-colors duration-300">
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-soma-muted hover:text-white transition-colors">Log in</Link>
              <Link to="/programs" data-testid="nav-cta"
                className="text-sm px-6 py-2.5 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300">
                Start training
              </Link>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden glass border-t border-white/10 mt-3">
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => l.to ? (
                <Link key={l.label} to={l.to} className="text-soma-muted hover:text-white">{l.label}</Link>
              ) : (
                <a key={l.label} href={`/#${l.section}`} onClick={goToSection(l.section)} className="text-soma-muted hover:text-white">{l.label}</a>
              ))}
              {user ? (
                <>
                  {user.role === "admin" && <Link to="/admin" className="text-soma-accent">Admin</Link>}
                  <Link to="/dashboard" className="text-white">Dashboard</Link>
                  <button onClick={() => { logout(); nav("/"); }} className="text-left text-soma-muted">Log out</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-white">Log in</Link>
                  <Link to="/programs" className="text-soma-primary font-semibold">Start training</Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
